import { useMemo } from 'react';

import { useUserProfile } from './user-profile';

import { usePermissions } from '~/client/contexts/permissions-context';
import {
  PLATFORM_MANAGE_ORGANIZATIONS,
  PLATFORM_MANAGE_SYSTEM_ADMINS,
} from '~/shared/constants/capabilities';

export type OrgRole =
  | 'ORG_ADMIN'
  | 'COMPLIANCE_MANAGER'
  | 'AUDIT_MANAGER'
  | 'ASSURANCE_MANAGER'
  | 'GOVERNANCE_MANAGER'
  | 'MEMBER';

/**
 * Authorization Hook
 *
 * Provides unified authorization checks for:
 * - Platform-level permissions (via PBAC `platform:*` capabilities)
 * - Organization-level permissions (org role strings + PBAC where applicable)
 * - Feature flags (beta user access)
 *
 * Prefer `usePermissions().hasCapability(...)` for new UI gates; this hook
 * integrates profile-derived role checks with the capability list from `myPermissions`.
 */
export function useAuthorization() {
  const { userProfile, isAuthenticated } = useUserProfile();
  const { hasCapability } = usePermissions();

  const authorization = useMemo(
    () => ({
      /**
       * Platform operator: holds `platform:manage_system_admins` (from PBAC matrix / overrides).
       */
      isSystemAdmin: (): boolean => {
        return hasCapability('platform:manage_system_admins');
      },

      /**
       * Check if user has access to beta features
       */
      isBetaUser: (): boolean => {
        return userProfile.isBetaUser ?? false;
      },

      /**
       * Check if user has any of the specified organization roles in their current organization
       */
      hasOrgRole: (roles: OrgRole[]): boolean => {
        if (!userProfile.currentOrganizationRole) {
          return false;
        }
        return roles.includes(userProfile.currentOrganizationRole as OrgRole);
      },

      /**
       * Org-wide member administration (replaces raw ORG_ADMIN string checks).
       */
      isOrgAdmin: (): boolean => {
        return hasCapability(PLATFORM_MANAGE_ORGANIZATIONS);
      },

      /**
       * Check if user is a Compliance Manager in their current organization
       */
      isComplianceManager: (): boolean => {
        return (
          (userProfile.currentOrganizationRole as string) ===
          'COMPLIANCE_MANAGER'
        );
      },

      /**
       * Check if user is an Audit Manager in their current organization
       */
      isAuditManager: (): boolean => {
        return (
          (userProfile.currentOrganizationRole as string) === 'AUDIT_MANAGER'
        );
      },

      /**
       * Any non-MEMBER org role from profile (legacy heuristic for feature flags).
       */
      hasElevatedOrgRole: (): boolean => {
        return [
          'ORG_ADMIN',
          'COMPLIANCE_MANAGER',
          'AUDIT_MANAGER',
          'ASSURANCE_MANAGER',
          'GOVERNANCE_MANAGER',
        ].includes((userProfile.currentOrganizationRole as string) ?? '');
      },

      isAuthenticated: (): boolean => {
        return isAuthenticated;
      },

      canAccessBetaFeatures: (): boolean => {
        return userProfile.isBetaUser ?? false;
      },

      /**
       * Organization admin dashboard / org-wide admin affordances (PBAC).
       */
      canAccessOrgAdmin: (): boolean => {
        return (
          hasCapability(PLATFORM_MANAGE_SYSTEM_ADMINS) ||
          hasCapability(PLATFORM_MANAGE_ORGANIZATIONS)
        );
      },

      getCurrentOrgRole: (): OrgRole | undefined => {
        return userProfile.currentOrganizationRole as OrgRole | undefined;
      },

      hasPermission: (params: {
        requireSystemAdmin?: boolean;
        requireBeta?: boolean;
        requireOrgRoles?: OrgRole[];
        requireOrgAdmin?: boolean;
        requireElevatedOrgRole?: boolean;
      }): boolean => {
        const {
          requireSystemAdmin,
          requireBeta,
          requireOrgRoles,
          requireOrgAdmin,
          requireElevatedOrgRole,
        } = params;

        if (
          requireSystemAdmin &&
          !hasCapability(PLATFORM_MANAGE_SYSTEM_ADMINS)
        ) {
          return false;
        }

        if (requireBeta && !(userProfile.isBetaUser ?? false)) {
          return false;
        }

        if (requireOrgAdmin && !hasCapability(PLATFORM_MANAGE_ORGANIZATIONS)) {
          return false;
        }

        if (requireElevatedOrgRole) {
          if (
            ![
              'ORG_ADMIN',
              'COMPLIANCE_MANAGER',
              'AUDIT_MANAGER',
              'ASSURANCE_MANAGER',
              'GOVERNANCE_MANAGER',
            ].includes((userProfile.currentOrganizationRole as string) ?? '')
          ) {
            return false;
          }
        }

        if (requireOrgRoles && requireOrgRoles.length > 0) {
          if (!userProfile.currentOrganizationRole) {
            return false;
          }
          if (
            !requireOrgRoles.includes(
              userProfile.currentOrganizationRole as OrgRole
            )
          ) {
            return false;
          }
        }

        return true;
      },
    }),
    [userProfile, isAuthenticated, hasCapability]
  );

  return authorization;
}
